import moment from 'moment-timezone';
import { momentLocalizer } from 'react-big-calendar';

/**
 * Builds the react-big-calendar localizer used by the Calendar, patched so the
 * time grid is a fixed 24-hour grid regardless of daylight-saving time.
 *
 * Why this exists: react-big-calendar sizes the week's shared time gutter from
 * the first day of the week (Sunday). US/EU DST changes always fall on a
 * Sunday, so that day is 23h (spring-forward) or 25h (fall-back). The stock
 * moment localizer measures slot metrics with real elapsed time, so the gutter
 * gets 23 or 25 rows (a skipped or duplicated hour). Every other day that week
 * still lays out on 24 rows, so events drift an hour from the gutter labels —
 * the "times in the left column are an hour off, events are correct" bug.
 *
 * Fix: drive every slot-metric method from wall-clock minutes in the configured
 * zone, so the grid is always 24 rows and events sit on their wall-clock row.
 *
 * All four methods are consumed only by react-big-calendar's TimeSlots via the
 * localizer instance, so overriding them here is sufficient. Tagged
 * `RBC-LOCALIZER-PATCH`.
 */
export const calendarLocalizer = (timezone?: string) => {
  const localizer = momentLocalizer(moment);

  const toZone = (date: Date) =>
    timezone ? moment.tz(date, timezone) : moment(date);

  // Minutes since midnight by the wall clock (DST-agnostic): noon is always 720
  // even on a 23h/25h day.
  const wallMinutes = (date: Date) => {
    const m = toZone(date);
    return m.hours() * 60 + m.minutes();
  };

  // RBC-LOCALIZER-PATCH(dst): KEEP after moment→dayjs (the dayjs localizer has
  // the same real-elapsed slot metrics). Present a fixed 24-row wall-clock grid.

  // Drives the number of gutter rows: wall-clock span is 1439 for a full day
  // every day, so a DST Sunday no longer adds/drops a row.
  localizer.getTotalMin = (start: Date, end: Date) =>
    wallMinutes(end) - wallMinutes(start);

  // Slot base for the day; always 0 for a midnight min.
  localizer.getMinutesFromMidnight = (start: Date) => wallMinutes(start);

  // Corrects real elapsed minutes to wall-clock minutes so an event on the
  // transition day still lands on its wall-clock row (positionFromDate adds
  // this to localizer.diff).
  localizer.getDstOffset = (start: Date, end: Date) =>
    wallMinutes(end) -
    wallMinutes(start) -
    toZone(end).diff(toZone(start), 'minutes');

  // Builds each gutter slot from wall-clock hour:minute so labels read
  // 12am…11pm once each (no duplicated/ skipped hour from DST arithmetic).
  localizer.getSlotDate = (
    dt: Date,
    minutesFromMidnight: number,
    offset: number
  ) => {
    const total = minutesFromMidnight + offset;
    return toZone(dt)
      .startOf('day')
      .hours(Math.floor(total / 60))
      .minutes(total % 60)
      .toDate();
  };

  return localizer;
};
